import { projectTitleProgress, titleRequiredCount, fishProgressChanges, titleAvailabilityIncluded } from "./title-progress.js";
import { titleEvidenceMarkup } from "./title-evidence.js";
import { titleDescriptionParts, titleColorCss } from "./title-presentation.js";
import { createFishydexDetailSessionRepository } from "./detail-session-repository.js";
import { createTitleDashboard } from "./title-dashboard.js";
import { createFishingTitlesApi } from "./fishing-titles-api.js";

function element(tag, className = "", text = "") {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}
function icon(name) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "fishy-icon fishy-icon--inline size-6");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const use = document.createElementNS(svg.namespaceURI, "use");
  use.setAttribute("href", `#fishy-${name}`);
  use.setAttribute("width", "100%");
  use.setAttribute("height", "100%");
  svg.append(use);
  return svg;
}
function acquisitionPresentation(button, owned) {
  const previous = button.getAttribute("aria-pressed");
  if (previous !== String(owned)) {
    button.dataset.titleAnimation = String(owned && previous === "false");
  }
  button.setAttribute("aria-pressed", String(owned));
}
function trophyPresentation(owned) {
  return owned ? "btn-soft btn-info"
    : "btn-ghost text-base-content/25 hover:text-base-content/60 focus-visible:text-base-content/60";
}
const itemId = (entry) => Number(entry?.item_id ?? entry?.itemId);
const dried = (entry) => Boolean(entry?.is_dried ?? entry?.isDried);
const PROGRESS_FRAME_CLASSES = Object.freeze([
  "relative",
  "border-0!",
  "[padding:var(--fishy-item-border-width)]!",
]);

function attribute(node, name, value) {
  const next = String(value);
  if (node.getAttribute(name) !== next) node.setAttribute(name, next);
}

function content(node, value) {
  if (node.textContent !== value) node.textContent = value;
}

/** Owns the catalog request, controls, subscriptions and teardown; never owns persisted progress. */
export function mountTitleControls({ root, repository, resolveApiUrl, t, guideCount, encyclopediaKey, onCatalog }) {
  const api = createFishingTitlesApi({ resolveApiUrl });
  const panel = root.querySelector("#fishydex-title-controls");
  const countControl = root.querySelector("[data-title-count-control]");
  const catalogStatus = root.querySelector("[data-title-catalog-status]");
  const preferences = [
    { node: root.querySelector("[data-title-contest-visibility]"), field: "showContestTitles", command: "setShowContestTitles" },
    { node: root.querySelector("[data-title-unobtainable-visibility]"), field: "showUnobtainableTitles", command: "setShowUnobtainableTitles" },
  ];
  const otherTitles = root.querySelector("[data-title-other]");
  let projection = repository.state();
  let catalog = null;
  let catalogKind = "loading";
  let catalogKey = null;
  let catalogGeneration = 0;
  let request = null;
  let reportRequest = null;
  let byFish = new Map();
  let titleById = new Map();
  let entries = new Map();
  let selected = null;
  let disposed = false;
  let mutationQueue = Promise.resolve();
  let panelKey = "";
  let panelBindings = null;
  let fishSource = null;
  const cardByFish = new Map();
  let otherTitleBindings = [];
  let otherTitlesKey = "";
  let message = "";
  const reportMessages = new Map();
  let currentLang = "";
  let currentRevision = "";
  let dataHtmlLang = "";
  let uiLocale = "";
  let formatter = new Intl.NumberFormat();
  const cards = new WeakMap();
  const imageProgress = new WeakMap();
  const controls = new AbortController();
  const detailTabs = Array.from(root.querySelectorAll("[data-fish-detail-tab]"));
  const detailSession = createFishydexDetailSessionRepository();
  const initialTab = detailSession.load();
  const tabStatus = element("p", "text-xs text-error");
  tabStatus.setAttribute("role", "status");
  tabStatus.hidden = true;
  detailTabs[0]?.parentElement.after(tabStatus);
  function reflectTabPersistence(result) {
    tabStatus.hidden = result.kind === "ready" || result.kind === "missing";
    tabStatus.textContent = tabStatus.hidden ? "" : t("fishydex.titles.tab_memory_error");
  }
  reflectTabPersistence(initialTab);
  const detailPanels = Array.from(root.querySelectorAll("[data-fish-detail-panel]"));
  const staticZones = root.querySelector(".fishydex-static-details .fishydex-details-zones-panel");
  const staticZonesParent = staticZones?.parentNode;
  const staticZonesNext = staticZones?.nextSibling;
  if (staticZones) root.querySelector("#fishydex-details-panel-zones")?.append(staticZones);
  function selectDetailTab(kind, focus = false, remember = true) {
    for (const tab of detailTabs) {
      const active = tab.dataset.fishDetailTab === kind;
      tab.classList.toggle("tab-active", active);
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    }
    for (const panel of detailPanels) panel.hidden = panel.dataset.fishDetailPanel !== kind;
    if (remember) reflectTabPersistence(detailSession.selectTab(kind));
  }
  for (const [index, tab] of detailTabs.entries()) {
    tab.addEventListener("click", () => selectDetailTab(tab.dataset.fishDetailTab), { signal: controls.signal });
    tab.addEventListener("keydown", (event) => {
      const next = event.key === "Home" ? 0 : event.key === "End" ? detailTabs.length - 1
        : event.key === "ArrowRight" ? (index + 1) % detailTabs.length
          : event.key === "ArrowLeft" ? (index + detailTabs.length - 1) % detailTabs.length : null;
      if (next === null) return;
      event.preventDefault();
      selectDetailTab(detailTabs[next].dataset.fishDetailTab, true);
    }, { signal: controls.signal });
  }
  selectDetailTab(initialTab.kind === "ready" ? initialTab.tab : "info", false, false);
  root.querySelector("[data-fish-detail-tabs-ready]")?.setAttribute("data-fish-detail-tabs-ready", "true");
  const listen = (node, event, callback) => node.addEventListener(event, callback);
  const text = (key, vars) => t(`fishydex.titles.${key}`, vars);
  const number = (value) => formatter.format(value);
  const dashboard = createTitleDashboard({ root, text, t, guideCount });
  const titleList = (entry, kind) => (byFish.get(itemId(entry)) || []).filter((title) => (!kind || title.kind === kind) && titleAvailabilityIncluded(title, projection));
  const progress = (entry) => projectTitleProgress(entry, titleList(entry, "catch"), projection, guideCount(entry));
  const titleOptions = (entry) => {
    const key = encyclopediaKey(entry);
    return { titles: titleList(entry, "catch"), ...(Number.isInteger(key) && key > 0 ? { encyclopediaKey: key } : {}) };
  };
  const countText = (count) => count.kind === "known" ? number(count.count) : text("unknown");
  const titleName = (title) => title.name || text("name_unknown", { id: title.titleId });
  const ownedText = (owned) => text(owned ? "owned" : "not_owned");
  const requirementText = (title, compact = false) => title.requirement
    ? text(compact ? "required_catches" : "reference_count", { count: title.requirement.minimum === title.requirement.maximum
      ? number(title.requirement.minimum) : `${number(title.requirement.minimum)}–${number(title.requirement.maximum)}` })
    : titleRequiredCount(title) !== null
      ? text(compact ? "estimated_catches" : "community_count", { count: number(titleRequiredCount(title)) })
      : text(compact ? "unknown" : "requirement_unknown");
  function statusNode(value, error = false) {
    const node = element("p", error ? "alert alert-error text-sm" : "text-sm", value);
    node.setAttribute("role", "status");
    return node;
  }
  function mutate(operation) {
    const run = async () => {
      if (disposed) return;
      message = "";
      try {
        const acknowledgement = await operation();
        if (!disposed && acknowledgement.kind !== "ready") message = text("save_error");
      } catch (_error) {
        if (!disposed) message = text("save_error");
      } finally {
        if (!disposed) {
          // Subscription owns acknowledged count rendering. Completion only
          // presents errors, including failures that emit no state event.
          renderStatus();
          updateOtherTitleState();
          updatePanelState();
        }
      }
    };
    mutationQueue = mutationQueue.then(run, run);
    return mutationQueue;
  }
  function setMutationDisabled(node, disabled) {
    if (node.disabled !== disabled) node.disabled = disabled;
  }
  function refreshDisabled() {
    const disabled = projection.kind !== "ready";
    root.querySelectorAll("[data-title-mutation]").forEach((node) => {
      setMutationDisabled(node, disabled || node.dataset.titleUnknown === "true");
    });
  }
  function renderCard(card, entry) {
    if (dried(entry)) return;
    const catchTitles = titleList(entry, "catch");
    const contestTitles = titleList(entry, "contest");
    const state = progress(entry);
    const owned = new Set(projection.obtainedTitleIds || []);
    const trophyOwned = contestTitles.some((title) => owned.has(title.titleId));
    const showTrophy = contestTitles.length > 0;
    const key = JSON.stringify([catalogKey, catalogKind, uiLocale, showTrophy, trophyOwned]);
    if (cards.get(card) === key) {
      renderImageProgress(card.querySelector(".fishydex-icon-wrap"), entry, state, catchTitles);
      return;
    }
    cards.set(card, key);
    let button = card.querySelector("[data-title-trophy]");
    if (!showTrophy) {
      button?.remove();
    } else {
      if (!button) {
        button = element("button");
        button.type = "button";
        button.dataset.titleTrophy = "";
        button.dataset.titleMutation = "";
        button.append(icon("trophy-line"));
        listen(button, "click", (event) => {
          event.stopPropagation();
          const title = titleList(entry, "contest")[0];
          void mutate(() => repository.setTitleOwned(
            title, !repository.state().obtainedTitleIds.includes(title.titleId),
          ));
        });
        card.querySelector(".fishydex-card-top")?.classList.add("relative");
        card.querySelector(".fishydex-card-top")?.insertBefore(button, card.querySelector(".fishydex-card-actions"));
      }
      attribute(button, "class", `btn btn-circle btn-lg sm:btn-md ${trophyPresentation(trophyOwned)} pointer-events-auto absolute left-1/2 -translate-x-1/2 z-2`);
      button.style.color = trophyOwned ? titleColorCss(contestTitles[0]) : "";
      acquisitionPresentation(button, trophyOwned);
      attribute(button, "aria-label", titleName(contestTitles[0]));
      attribute(button, "data-fishy-tooltip", titleName(contestTitles[0]));
      attribute(button, "data-fishy-tooltip-color", titleColorCss(contestTitles[0]));
      attribute(button, "data-fishy-tooltip-mode", "anchor-only");
      attribute(button.querySelector("use"), "href", trophyOwned ? "#fishy-trophy-fill" : "#fishy-trophy-line");
    }
    renderImageProgress(card.querySelector(".fishydex-icon-wrap"), entry, state, catchTitles);
  }
  function renderImageProgress(frame, entry, state, titles) {
    if (!frame) return;
    const enabled = Boolean(entry && !dried(entry) && titles.length && catalogKind === "ready");
    const key = JSON.stringify([catalogKey, catalogKind, catalogGeneration, uiLocale, itemId(entry), projection.showUnobtainableTitles]);
    const previous = imageProgress.get(frame);
    if (previous?.key === key) {
      previous.update?.(state);
      return;
    }
    // Replace the existing border inside its original footprint. Equal padding
    // preserves the artwork size. Hover targets never mutate progress.
    for (const className of PROGRESS_FRAME_CLASSES) frame.classList.toggle(className, enabled);
    frame.toggleAttribute("data-title-border", enabled);
    frame.querySelector("[data-title-outline]")?.remove();
    if (!enabled) { imageProgress.set(frame, { key }); return; }
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.dataset.titleOutline = "";
    svg.setAttribute("viewBox", "0 0 100 100");
    svg.setAttribute("class", "pointer-events-none absolute inset-0 z-1 size-full");
    svg.setAttribute("role", "img");
    svg.setAttribute("focusable", "false");
    const perimeter = "M50 3H66A31 31 0 0 1 97 34V66A31 31 0 0 1 66 97H34A31 31 0 0 1 3 66V34A31 31 0 0 1 34 3H50Z";
    const segmentSize = 100 / titles.length;
    const gap = titles.length > 1 ? Math.min(2, segmentSize / 5) : 0;
    const length = segmentSize - gap;
    const path = (start, amount) => {
      const node = document.createElementNS(svg.namespaceURI, "path");
      node.setAttribute("d", perimeter);
      node.setAttribute("pathLength", "100");
      node.setAttribute("fill", "none");
      node.setAttribute("stroke", "var(--fishy-item-grade-color)");
      node.setAttribute("stroke-width", "3");
      node.setAttribute("stroke-linecap", "round");
      node.setAttribute("stroke-dasharray", `${amount} ${100 - amount}`);
      node.setAttribute("stroke-dashoffset", String(-start));
      return node;
    };
    const bindings = titles.map((title, index) => {
      const group = document.createElementNS(svg.namespaceURI, "g");
      group.setAttribute("class", "group/title-segment");
      const start = index * segmentSize + gap / 2;
      const track = path(start, length);
      track.setAttribute("opacity", "0.3");
      const fill = path(start, length);
      fill.dataset.titleSegment = String(title.titleId);
      track.setAttribute("class", "transition-[stroke-width,opacity] duration-150 ease-out motion-reduce:transition-none group-hover/title-segment:[stroke-width:6] group-hover/title-segment:opacity-60");
      fill.setAttribute("class", "transition-[stroke-dasharray,opacity,stroke-width] duration-300 ease-out motion-reduce:transition-none group-hover/title-segment:[stroke-width:6]");
      const hit = path(start, length);
      hit.setAttribute("stroke", "transparent");
      hit.setAttribute("stroke-width", "12");
      hit.setAttribute("class", "cursor-pointer pointer-events-none [@media(hover:hover)]:[pointer-events:stroke]");
      hit.dataset.titleHover = String(title.titleId);
      const position = (index + 0.5) / titles.length;
      hit.dataset.fishyTooltipHeading = titleName(title);
      listen(hit, "click", (event) => {
        if (event.defaultPrevented || event.button > 0
          || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        selectDetailTab("titles");
      });
      hit.dataset.fishyTooltipColor = titleColorCss(title);
      hit.dataset.fishyTooltipMode = "anchor-only";
      hit.dataset.fishyTooltipPathPosition = String(position);
      hit.dataset.fishyTooltipPlacement = position < 0.125 || position >= 0.875 ? "top"
        : position < 0.375 ? "right" : position < 0.625 ? "bottom" : "left";
      group.append(track, fill, hit);
      svg.append(group);
      return { fill, hit, title };
    });
    frame.append(svg);
    const update = (next) => {
      attribute(svg, "aria-label", `${text("image_progress")}. ${text("current_count", { count: countText(next.count) })}. ${text("owned_total", { owned: number(next.ownedTitleIds.length), total: number(titles.length) })}`);
      for (const [index, { fill, hit, title }] of bindings.entries()) {
        const requirement = requirementText(title, true);
        const description = next.count.kind === "known"
          ? `${countText(next.count)}/${requirement}`
          : requirement;
        attribute(hit, "data-fishy-tooltip", description);
        attribute(hit, "aria-label", `${titleName(title)}. ${description}`);
        const segment = next.segments[index];
        const amount = length * (segment.fill ?? 0);
        attribute(fill, "stroke-dasharray", `${amount} ${100 - amount}`);
        attribute(fill, "opacity", amount > 0 ? "1" : "0");
        attribute(fill, "data-title-fill", segment.fill === null ? "unknown" : segment.fill);
      }
    };
    imageProgress.set(frame, { key, update });
    update(state);
  }
  function field(parent, key, control) {
    const label = element("label", "grid gap-2 min-w-0");
    label.append(element("span", "text-sm", text(key)), control);
    parent.append(label);
    return control;
  }
  function countInput(value) {
    const input = element("input", "input input-lg min-h-11 sm:input-sm sm:min-h-0 w-full");
    input.type = "number";
    input.inputMode = "numeric";
    input.min = "0";
    input.max = "4294967295";
    input.step = "1";
    input.required = true;
    input.value = value == null ? "" : String(value);
    return input;
  }
  function renderReport(title) {
    const reportRow = element("tr");
    reportRow.id = `fishydex-title-report-${title.titleId}`;
    const reportCell = element("td", "whitespace-normal");
    reportCell.colSpan = 3;
    reportRow.append(reportCell);
    const trigger = element("button", "btn btn-ghost btn-circle min-h-11 min-w-11");
    trigger.type = "button";
    trigger.dataset.titleReportToggle = String(title.titleId);
    trigger.setAttribute("aria-controls", reportRow.id);
    trigger.setAttribute("aria-expanded", String(reportMessages.has(title.titleId)));
    reportRow.hidden = !reportMessages.has(title.titleId);
    listen(trigger, "click", () => {
      reportRow.hidden = !reportRow.hidden;
      trigger.setAttribute("aria-expanded", String(!reportRow.hidden));
    });
    trigger.setAttribute("aria-label", text("report_heading"));
    trigger.dataset.fishyTooltip = text("report_heading");
    trigger.dataset.fishyTooltipMode = "anchor-only";
    trigger.append(icon("nav-feedback"));
    const form = element("form", "grid gap-3 mt-3 rounded-box border border-base-300 bg-base-100 p-3");
    form.append(element("p", "text-sm", text("report_help")));
    const input = field(form, "report_count", countInput(null));
    const date = element("input", "input input-sm w-full");
    date.type = "date";
    field(form, "report_date", date);
    const submit = element("button", "btn btn-lg min-h-11 sm:btn-sm sm:min-h-0 justify-self-start", text("report_submit"));
    submit.type = "submit";
    const status = statusNode(reportMessages.has(title.titleId) ? text(reportMessages.get(title.titleId)) : "");
    form.append(submit, status);
    listen(form, "submit", async (event) => {
      event.preventDefault();
      if (reportRequest || !form.reportValidity()) return;
      const controller = new AbortController();
      reportRequest = controller;
      submit.disabled = true;
      status.textContent = text("report_sending");
      const reportedItem = itemId(selected);
      try {
        await api.report({ titleId: title.titleId, catchCount: input.valueAsNumber, obtainedAt: date.value || null }, { signal: controller.signal });
        if (!disposed && itemId(selected) === reportedItem) {
          reportMessages.set(title.titleId, "report_saved");
          status.textContent = text("report_saved");
          await loadCatalog(currentLang, currentRevision);
        }
      } catch (error) {
        if (!disposed && !controller.signal.aborted) {
          const key = error.message === "signedOut" ? "report_sign_in" : "report_error";
          reportMessages.set(title.titleId, key);
          status.textContent = text(key);
          status.className = "alert alert-error text-sm";
        }
      } finally {
        if (reportRequest === controller) reportRequest = null;
        submit.disabled = false;
      }
    });
    reportRow.dataset.titleReport = String(title.titleId);
    reportCell.append(form);
    return { trigger, row: reportRow };
  }
  function titleTable() {
    const table = element("table", "table table-sm table-fixed min-w-[36rem] w-full");
    table.append(element("caption", "sr-only", text("table_caption")));
    const head = element("thead");
    const headings = element("tr");
    for (const key of ["title_column", "description_column", "requirement_column"]) {
      const heading = element("th", "whitespace-normal", text(key));
      heading.scope = "col";
      headings.append(heading);
    }
    head.append(headings);
    const body = element("tbody");
    table.append(head, body);
    const scroll = element("div", "min-w-0 overflow-x-auto");
    scroll.tabIndex = 0;
    scroll.setAttribute("role", "region");
    scroll.setAttribute("aria-label", text("table_caption"));
    scroll.append(table);
    return { scroll, body };
  }
  function titleRow(title) {
    const row = element("tr");
    row.dataset.titleId = String(title.titleId);
    const name = element("th", "whitespace-normal break-words font-semibold");
    name.scope = "row";
    const titleLine = element("div", "flex items-center gap-2");
    const titleLabel = element("span", "min-w-0", titleName(title));
    titleLabel.style.color = titleColorCss(title);
    if (title.name && dataHtmlLang) titleLabel.lang = dataHtmlLang;
    titleLine.append(titleLabel);
    name.append(titleLine);
    if (title.availability?.obtainable === false) {
      const availability = element("span", "inline-flex items-center gap-1 text-xs font-normal text-base-content/60", text("unobtainable"));
      availability.prepend(icon("ghost-fill"));
      name.append(availability);
    }
    const source = titleDescriptionParts(title.description);
    const descriptionText = title.kind === "catch"
      ? [source.condition, source.description].filter(Boolean).join("\n")
      : source.description;
    const description = element("td", "whitespace-pre-line break-words text-sm", descriptionText || text("description_unknown"));
    description.dataset.titleDescription = "";
    if (title.description !== null && dataHtmlLang) description.lang = dataHtmlLang;
    const requirement = element("td", "whitespace-normal text-sm");
    requirement.dataset.titleRequirement = "";
    const ownedLabel = element("span", "sr-only");
    row.append(name, description, requirement);
    return { row, titleLine, source, requirement, ownedLabel };
  }
  function updateAcquisitionButton(button, title, checked) {
    attribute(button, "class", `fishydex-caught-button btn btn-circle shadow-none shrink-0 min-h-11 min-w-11 ${checked ? "btn-soft btn-success is-caught" : "btn-ghost"}`);
    acquisitionPresentation(button, checked);
    const label = text(checked ? "mark_not_obtained" : "mark_obtained");
    attribute(button, "aria-label", `${label}: ${titleName(title)}`);
    attribute(button, "data-fishy-tooltip", ownedText(checked));
  }
  function renderPanel() {
    if (!panel) return;
    panel.hidden = !selected;
    const imageFrame = root.querySelector("#fishydex-details-icon-frame");
    if (!selected) renderImageProgress(imageFrame, null, null, []);
    if (panel.hidden) { panelKey = ""; panelBindings = null; countControl.replaceChildren(); return; }
    const state = progress(selected);
    renderImageProgress(imageFrame, selected, state, titleList(selected, "catch"));
    const key = JSON.stringify([itemId(selected), catalogKey, catalogKind, catalogGeneration, uiLocale, projection.showUnobtainableTitles]);
    if (panelKey === key) { updatePanelState(state); return; }
    panelKey = key;
    const fragment = document.createDocumentFragment();
    const count = countInput(state.count.kind === "known" ? state.count.count : null);
    count.className = "input input-ghost min-h-11 w-28 text-right tabular-nums";
    count.placeholder = text("unknown");
    count.required = false;
    count.dataset.titleMutation = "";
    count.dataset.titleFocus = "manual-count";
    const bindings = { count, countDirty: false, editRevision: 0, submittedRevision: 0, titles: [] };
    panelBindings = bindings;
    const entry = selected;
    listen(count, "input", () => {
      bindings.countDirty = true;
      bindings.editRevision += 1;
    });
    const commitCount = async () => {
      if (!bindings.countDirty || !count.validity.valid
        || bindings.submittedRevision === bindings.editRevision) return;
      const revision = bindings.editRevision;
      bindings.submittedRevision = revision;
      const value = count.value === "" ? 0 : count.valueAsNumber;
      await mutate(() => repository.setCatchCount(itemId(entry), value, titleOptions(entry)));
      if (panelBindings === bindings && bindings.editRevision === revision && !message) {
        bindings.countDirty = false;
        updatePanelState();
      }
    };
    listen(count, "change", commitCount);
    listen(count, "blur", () => {
      // Native steppers can finish their change before focus leaves the field.
      // Commit any remaining draft once, including an explicit clear-to-zero.
      if (count.validity.valid) { void commitCount(); return; }
      bindings.countDirty = false;
      bindings.editRevision += 1;
      updatePanelState();
    });
    listen(count, "keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      count.blur();
    });
    panelBindings.status = statusNode("", true);
    panelBindings.countStatus = statusNode("", true);
    fragment.append(panelBindings.status);
    countControl.replaceChildren(count, panelBindings.countStatus);
    if (catalogKind !== "ready") {
      fragment.append(statusNode(text(catalogKind === "error" ? "catalog_error" : "loading"), catalogKind === "error"));
    } else {
      const titles = titleList(selected);
      if (!titles.length) fragment.append(element("p", "text-sm", text("no_titles")));
      const { scroll, body } = titleTable();
      for (const title of titles) {
        const { row, titleLine, source, requirement, ownedLabel } = titleRow(title);
        let checkbox = null;
        let acquire = null;
        let trophy = null;
        let countLine = null;
        if (title.kind === "catch") {
          countLine = element("div", "flex items-center gap-2");
          countLine.append(element("span", "min-w-0", requirementText(title, true)), ownedLabel);
          requirement.append(countLine);
          const minimum = titleRequiredCount(title);
          if (minimum !== null) {
            acquire = element("button", "btn btn-ghost btn-square shrink-0 min-h-11 min-w-11");
            acquire.type = "button";
            acquire.setAttribute("aria-label", `${text("mark_obtained")}: ${titleName(title)}`);
            acquire.dataset.fishyTooltip = ownedText(false);
            acquire.dataset.fishyTooltipMode = "anchor-only";
            acquire.append(icon("medal-fill"));
            acquire.dataset.titleMutation = "";
            acquire.dataset.titleAcquire = String(title.titleId);
            listen(acquire, "click", () => {
              const owned = !progress(entry).ownedTitleIds.includes(title.titleId);
              void mutate(() => repository.setTitleOwned(title, owned, titleOptions(entry)));
            });
            titleLine.prepend(acquire);
          }
        } else if (title.kind === "contest") {
          const condition = element("span", "whitespace-pre-line", source.condition ?? text("condition_unknown"));
          if (source.condition && dataHtmlLang) condition.lang = dataHtmlLang;
          requirement.append(condition, ownedLabel);
          trophy = element("button");
          trophy.type = "button";
          trophy.dataset.titleMutation = "";
          trophy.dataset.titleContest = String(title.titleId);
          trophy.setAttribute("aria-label", titleName(title));
          trophy.dataset.fishyTooltip = titleName(title);
          trophy.dataset.fishyTooltipColor = titleColorCss(title);
          trophy.dataset.fishyTooltipMode = "anchor-only";
          trophy.append(icon("trophy-line"));
          listen(trophy, "click", () => {
            void mutate(() => repository.setTitleOwned(title,
              !repository.state().obtainedTitleIds.includes(title.titleId), titleOptions(entry)));
          });
          titleLine.prepend(trophy);
        } else {
          const condition = element("span", "whitespace-pre-line", source.condition ?? text("condition_unknown"));
          if (title.description !== null && dataHtmlLang) condition.lang = dataHtmlLang;
          checkbox = element("input", "checkbox checkbox-sm shrink-0");
          checkbox.type = "checkbox";
          checkbox.dataset.titleMutation = "";
          checkbox.dataset.titleFocus = `owned-${title.titleId}`;
          const label = element("label", "flex min-h-11 cursor-pointer items-center gap-2");
          label.append(checkbox, condition);
          requirement.append(label, ownedLabel);
          listen(checkbox, "change", () => {
            const checked = checkbox.checked;
            void mutate(() => repository.setTitleOwned(title, checked, titleOptions(entry)));
          });
        }
        panelBindings.titles.push({ title, checkbox, acquire, trophy, ownedLabel, row });
        body.append(row);
        if (title.kind === "catch") {
          const actions = element("div", "flex shrink-0 items-center gap-1");
          const rail = document.createElement("template");
          rail.innerHTML = titleEvidenceMarkup(title, { text, number });
          actions.append(rail.content);
          const report = renderReport(title);
          actions.append(report.trigger);
          countLine.append(actions);
          body.append(report.row);
        }
      }
      fragment.append(scroll);
    }
    panel.replaceChildren(fragment);
    updatePanelState(state);
  }
  function updatePanelState(state = selected ? progress(selected) : null) {
    if (!panelBindings || !state) return;
    const { count, status, countStatus, titles } = panelBindings;
    const value = state.count.kind === "known" ? String(state.count.count) : "";
    if (!panelBindings.countDirty && count.value !== value) count.value = value;
    const failed = ["conflict", "invalid", "unsupportedVersion", "unavailable"].includes(projection.kind);
    content(status, message || (failed ? text("state_unavailable") : ""));
    status.hidden = !status.textContent;
    content(countStatus, status.textContent);
    countStatus.hidden = status.hidden;
    for (const { title, checkbox, acquire, trophy, ownedLabel, row } of titles) {
      const checked = title.kind === "catch" ? state.ownedTitleIds.includes(title.titleId)
        : (projection.obtainedTitleIds || []).includes(title.titleId);
      if (checkbox && checkbox.checked !== checked) checkbox.checked = checked;
      row.dataset.titleObtained = String(checked);
      if (acquire) updateAcquisitionButton(acquire, title, checked);
      if (trophy) {
        attribute(trophy, "class", `btn btn-circle shrink-0 min-h-11 min-w-11 ${trophyPresentation(checked)}`);
        trophy.style.color = checked ? titleColorCss(title) : "";
        acquisitionPresentation(trophy, checked);
        attribute(trophy.querySelector("use"), "href", checked ? "#fishy-trophy-fill" : "#fishy-trophy-line");
      }
      content(ownedLabel, ownedText(checked));
    }
  }

  function renderOtherTitles() {
    if (!otherTitles) return;
    const titles = catalog?.titles.filter((title) => title.itemId === null && titleAvailabilityIncluded(title, projection)) || [];
    otherTitles.hidden = catalogKind !== "ready" || titles.length === 0;
    const key = JSON.stringify([catalogKey, catalogKind, catalogGeneration, uiLocale, projection.showUnobtainableTitles]);
    if (key === otherTitlesKey) return;
    otherTitlesKey = key;
    const body = otherTitles.querySelector("[data-title-other-list]");
    if (!body) return;
    const fragment = document.createDocumentFragment();
    const { scroll, body: rows } = titleTable();
    otherTitleBindings = [];
    for (const title of titles) {
      const { row, titleLine, source, requirement, ownedLabel } = titleRow(title);
      const button = element("button");
      button.type = "button";
      button.dataset.titleMutation = "";
      button.dataset.titleFocus = `other-owned-${title.titleId}`;
      button.dataset.fishyTooltipMode = "anchor-only";
      button.append(icon("medal-fill"));
      titleLine.prepend(button);
      const condition = element("span", "whitespace-pre-line", title.kind === "catch"
        ? requirementText(title, true) : source.condition ?? text("condition_unknown"));
      if (title.kind !== "catch" && source.condition && dataHtmlLang) condition.lang = dataHtmlLang;
      requirement.append(condition, ownedLabel);
      otherTitleBindings.push({ title, button, ownedLabel, row });
      listen(button, "click", () => {
        void mutate(() => repository.setTitleOwned(title,
          !repository.state().obtainedTitleIds.includes(title.titleId)));
      });
      rows.append(row);
    }
    fragment.append(scroll);
    body.replaceChildren(fragment);
    updateOtherTitleState();
  }
  function updateOtherTitleState() {
    for (const { title, button, ownedLabel, row } of otherTitleBindings) {
      const checked = (projection.obtainedTitleIds || []).includes(title.titleId);
      updateAcquisitionButton(button, title, checked);
      row.dataset.titleObtained = String(checked);
      content(ownedLabel, ownedText(checked));
    }
  }

  function renderStatus() {
    for (const { node, field } of preferences) {
      if (node) node.checked = projection[field] === true;
    }
    if (catalogStatus) {
      catalogStatus.hidden = catalogKind === "ready" && !message;
      content(catalogStatus, message || text(catalogKind === "error" ? "catalog_error" : "loading"));
      attribute(catalogStatus, "class", catalogKind === "error" || message ? "alert alert-error text-sm" : "text-sm");
    }
  }
  function render(changes = null) {
    if (disposed) return;
    if (changes?.titleVisibilityChanged) changes = null;
    const changed = changes?.countFishIds ?? null;
    renderStatus();
    dashboard.update({ catalog, entries, projection, changes,
      contextKey: [catalogKey, catalogKind, catalogGeneration, uiLocale] });
    if (changed === null) {
      cardByFish.clear();
      root.querySelectorAll("#fishydex-grid [data-fish-id]").forEach((card) => {
        const id = Number(card.dataset.fishId);
        const entry = entries.get(id);
        if (entry) { cardByFish.set(id, card); renderCard(card, entry); }
      });
    } else {
      for (const id of changed) {
        const entry = entries.get(id);
        const card = cardByFish.get(id);
        if (entry && card?.isConnected) renderCard(card, entry);
      }
      for (const titleId of changes.titleIds) {
        const title = titleById.get(titleId);
        if (!title || (title.kind === "catch" && title.itemId !== null)) continue;
        const entry = entries.get(title.itemId);
        const card = cardByFish.get(title.itemId);
        if (entry && card?.isConnected) renderCard(card, entry);
      }
      if (changes.titleIds.size) updateOtherTitleState();
    }
    if (changed === null) renderOtherTitles();
    if (changed === null || changed.has(itemId(selected)) || changes.titleIds.size) renderPanel();
    if (changed === null) refreshDisabled();
  }
  async function loadCatalog(lang, revision) {
    catalogGeneration += 1;
    request?.abort();
    const controller = new AbortController();
    request = controller;
    catalogKind = "loading";
    catalog = null;
    onCatalog?.(null, catalogKind);
    byFish = new Map();
    titleById = new Map();
    render();
    try {
      const result = await api.catalog({ lang, revision, signal: controller.signal });
      if (disposed || request !== controller) return;
      catalog = result;
      titleById = new Map(result.titles.map((title) => [title.titleId, title]));
      for (const title of result.titles) {
        if (title.itemId === null) continue;
        const titles = byFish.get(title.itemId) || [];
        titles.push(title);
        byFish.set(title.itemId, titles);
      }
      for (const titles of byFish.values()) titles.sort((a, b) => a.ordinal - b.ordinal || a.titleId - b.titleId);
      catalogKind = "ready";
    } catch (_error) {
      if (disposed || controller.signal.aborted || request !== controller) return;
      catalogKind = "error";
    }
    if (!disposed) { onCatalog?.(catalog, catalogKind); render(); }
  }
  for (const { node, command } of preferences) {
    if (!node) continue;
    node.dataset.titleMutation = "";
    node.addEventListener("change", () => {
      const checked = node.checked;
      void mutate(() => repository[command](checked));
    }, { signal: controls.signal });
  }
  const unsubscribe = repository.subscribe((next) => {
    const previous = projection;
    const changed = fishProgressChanges(previous, next);
    projection = next;
    if (!disposed) render(changed);
  });
  return Object.freeze({
    discardCountDraft(fishId) {
      if (disposed || !panelBindings || itemId(selected) !== fishId) return;
      panelBindings.editRevision += 1;
      panelBindings.countDirty = false;
      updatePanelState();
    },
    mountCard(card, entry) {
      if (disposed) return;
      cardByFish.set(itemId(entry), card);
      renderCard(card, entry);
      card.querySelectorAll("[data-title-mutation]").forEach((node) => {
        setMutationDisabled(node, projection.kind !== "ready" || node.dataset.titleUnknown === "true");
      });
    },
    update({ fish, selectedFish = null, lang, revision = "", htmlLang = "", locale = "" }) {
      if (disposed) return;
      const catalogChanged = fishSource !== fish || currentLang !== lang
        || currentRevision !== revision || dataHtmlLang !== htmlLang || uiLocale !== locale;
      if (fishSource !== fish) {
        fishSource = fish;
        entries = new Map(fish.map((entry) => [itemId(entry), entry]));
      }
      if ((selected ? itemId(selected) : null) !== (selectedFish ? itemId(selectedFish) : null)) {
        reportRequest?.abort();
        reportRequest = null;
        reportMessages.clear();
        message = "";
      }
      selected = selectedFish;
      currentLang = lang;
      currentRevision = revision;
      dataHtmlLang = htmlLang;
      if (uiLocale !== locale) formatter = new Intl.NumberFormat(locale || undefined);
      uiLocale = locale;
      const nextKey = `${currentLang}:${revision}`;
      if (nextKey !== catalogKey) { catalogKey = nextKey; void loadCatalog(lang, revision); }
      if (catalogChanged) render();
      else renderPanel();
    },
    dispose() {
      disposed = true;
      request?.abort();
      reportRequest?.abort();
      controls.abort();
      tabStatus.remove();
      if (staticZones && staticZonesParent) staticZonesParent.insertBefore(staticZones, staticZonesNext || null);
      unsubscribe();
      panel?.replaceChildren();
      countControl?.replaceChildren();
      otherTitles?.querySelector("[data-title-other-list]")?.replaceChildren();
      root.querySelectorAll("[data-title-outline], [data-title-trophy]").forEach((node) => node.remove());
      root.querySelectorAll("[data-title-border]").forEach((frame) => {
        frame.classList.remove(...PROGRESS_FRAME_CLASSES);
        frame.removeAttribute("data-title-border");
      });
      entries.clear();
      byFish.clear();
      cardByFish.clear();
      titleById.clear();
      dashboard.dispose();
    },
  });
}
